import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Code2, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface EmbedWidgetGeneratorProps {
  occupationSlug: string;
  location?: string;
  className?: string;
}

/**
 * Lets publishers grab an <iframe> snippet for a salary widget. Each embed
 * includes a backlink to the full page, so widgets compound into free links.
 */
const EmbedWidgetGenerator = ({ occupationSlug, location = 'united-states', className = '' }: EmbedWidgetGeneratorProps) => {
  const [copied, setCopied] = useState(false);

  const src = `https://wageatlas.com/embed/salary/${occupationSlug}/${location}`;
  const snippet = `<iframe src="${src}" width="100%" height="320" style="border:0;max-width:480px" loading="lazy" title="Salary widget by Wage Atlas"></iframe>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Embed code copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select and copy manually.');
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Code2 className="h-5 w-5 text-wage-primary" />
          Embed this data
        </CardTitle>
        <CardDescription>Add this salary widget to your site - free to use.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <pre className="text-xs bg-muted rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">
          {snippet}
        </pre>
        <Button variant="outline" size="sm" className="w-full" onClick={handleCopy}>
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied ? 'Copied' : 'Copy embed code'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default EmbedWidgetGenerator;
